import { IconButton, InputAdornment } from "@mui/material"
import { CloseOutlined, SearchOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo } from "react"

import { useGetSalesProductSuggestions } from "../../hooks/useGetSalesProductSuggestions"
import type { SalesProduct } from "../../types"
import { SUGGESTION_ID_PREFIX, parseSuggestionId } from "../../utils"

import { AerosAutocomplete } from "@/components/UiKit"

interface ProductSearchSuggestionProps {
  searchInputValue: string
  onSearchInputChange: (value: string) => void
  onSearch: (value: string) => void
  onProductSelect: (product: SalesProduct) => void
  onClear: () => void
  excludedProductIds: Set<number>
}

export const ProductSearchSuggestion: FC<ProductSearchSuggestionProps> = ({
  searchInputValue,
  onSearchInputChange,
  onSearch,
  onProductSelect,
  onClear,
  excludedProductIds,
}) => {
  const { suggestions: suggestionProducts } = useGetSalesProductSuggestions(searchInputValue, excludedProductIds)

  const suggestions = useMemo(
    () =>
      suggestionProducts.map((product) => ({
        value: `${SUGGESTION_ID_PREFIX}${product.productId}`,
        label: product.vendorDescription ?? "",
      })),
    [suggestionProducts]
  )

  const handleAutocompleteChange = (value: string | null) => {
    const idString = parseSuggestionId(value)
    if (!idString) return
    const productId = Number(idString)
    const matched = suggestionProducts.find((product) => product.productId === productId)
    if (!matched) return
    onProductSelect(matched)
  }

  const handleSearchInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch(searchInputValue)
    }
  }

  return (
    <AerosAutocomplete
      freeSolo
      options={suggestions}
      inputValue={searchInputValue}
      onInputChange={onSearchInputChange}
      onChange={handleAutocompleteChange}
      forcePopupIcon={false}
      placeholder='Search Products'
      slotProps={{
        textField: {
          onKeyDown: handleSearchInputKeyDown,
          slotProps: {
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined className='text-gray-600' />
                </InputAdornment>
              ),
              endAdornment: searchInputValue ? (
                <InputAdornment position='end'>
                  <IconButton size='small' onClick={onClear} aria-label='Clear search'>
                    <CloseOutlined className='text-gray-600' />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          },
        },
      }}
      data-testid='sales.product-search-suggestion.search-field'
    />
  )
}
